import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal } from '../../molecules/Modal';
import { classService } from '../../../services/classService';
import { adminService } from '../../../services/adminService';
import { useAuthStore } from '../../../store/authStore';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Button = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;
  color: white;

  &:hover:not(:disabled) {
    background-color: #5a6268;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #007bff;
  color: white;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 13px;
  margin-top: 4px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
`;

const AddClassModal = ({ isOpen, onClose, onSuccess }) => {
  const user = useAuthStore(state => state.user);
  const [formData, setFormData] = useState({
    studentId: '',
    title: '',
    description: '',
    scheduledStartTime: '',
    duration: 60
  });

  const [students, setStudents] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch students when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchStudents();
    }
  }, [isOpen]);

  const fetchStudents = async () => {
    try {
      const allUsers = await adminService.getAllUsers();
      const studentList = allUsers.filter(u => u.type === 'mom');
      setStudents(studentList);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setErrors({ fetch: '학부모 목록을 불러오는데 실패했습니다.' });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        studentId: '',
        title: '',
        description: '',
        scheduledStartTime: '',
        duration: 60
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration' ? parseInt(value) || 0 : value
    }));

    // Clear error message
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.studentId) {
      newErrors.studentId = '학부모를 선택해주세요.';
    }
    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    }
    if (!formData.scheduledStartTime) {
      newErrors.scheduledStartTime = '시작 시간을 선택해주세요.';
    } else {
      const selectedTime = new Date(formData.scheduledStartTime);
      const now = new Date();
      if (selectedTime <= now) {
        newErrors.scheduledStartTime = '시작 시간은 현재 시각 이후여야 합니다.';
      }
    }
    if (formData.duration < 1) {
      newErrors.duration = '수업 시간은 1분 이상이어야 합니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const classData = {
        title: formData.title,
        description: formData.description,
        scheduledStartTime: new Date(formData.scheduledStartTime),
        duration: formData.duration
      };

      await classService.createClass(
        formData.studentId,
        user.uid,
        classData
      );

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Failed to create class:', error);
      setErrors({ submit: error.message || '수업 생성에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  // Get min datetime for input (current time)
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="새 수업 생성">
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>학부모 *</Label>
          <Select
            name="studentId"
            value={formData.studentId}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">학부모를 선택하세요</option>
            {students.map((student) => (
              <option key={student.id} value={student.id}>
                {student.name} ({student.email})
              </option>
            ))}
          </Select>
          {errors.studentId && <ErrorMessage>{errors.studentId}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>제목 *</Label>
          <Input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="예: 언어 발달 수업"
            disabled={loading}
          />
          {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>설명</Label>
          <Textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="수업에 대한 설명을 입력하세요 (선택사항)"
            disabled={loading}
          />
        </FormGroup>

        <FormGroup>
          <Label>시작 시간 *</Label>
          <Input
            type="datetime-local"
            name="scheduledStartTime"
            value={formData.scheduledStartTime}
            onChange={handleChange}
            min={getMinDateTime()}
            disabled={loading}
          />
          {errors.scheduledStartTime && <ErrorMessage>{errors.scheduledStartTime}</ErrorMessage>}
          <HelpText>Zoom 수업이 시작될 날짜와 시간을 선택하세요.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>수업 시간 (분) *</Label>
          <Input
            type="number"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            min="1"
            disabled={loading}
          />
          {errors.duration && <ErrorMessage>{errors.duration}</ErrorMessage>}
          <HelpText>수업이 진행될 예정 시간을 분 단위로 입력하세요.</HelpText>
        </FormGroup>

        {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}
        {errors.fetch && <ErrorMessage>{errors.fetch}</ErrorMessage>}

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose} disabled={loading}>
            취소
          </CancelButton>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? '생성 중...' : '수업 생성'}
          </SubmitButton>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

export default AddClassModal;
